import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { media } from 'helpers'
import styled from 'styled-components'

import CommentList from 'js/components/CommentList'
import CoverImage from 'js/components/CoverImage'
import Document from 'js/components/Document'
import { Col, Container, Row } from 'js/components/Grid/Grid'
import { H2, H4 } from 'js/components/Heading'
import { H1 } from 'js/components/Heading'
import ToggleTranslation from 'js/components/Item/components/ToggleTranslation'
import ItemStatusTag from 'js/components/Item/ItemStatusTag'
import ItemSuggestedItems from 'js/components/Item/ItemSuggestedItems'
import ItemTags from 'js/components/ItemTags'
import LikeAndBookmark from 'js/components/LikeAndBookmark'
import Section from 'js/components/Section/Section'
import SocialShare from 'js/components/SocialShare/SocialShare'
import Text from 'js/components/Text/Text'
import TiptapView from 'js/components/Tiptap/TiptapView'
import { showDateTime, showFullDate } from 'js/lib/helpers/date/showDate'
import { useSiteStore } from 'js/lib/stores/SiteStore'
import ThemeProvider from 'js/theme/ThemeProvider'

import ItemHeader from './ItemHeader'

const Wrapper = styled(Section)`
    padding-top: 0;

    .ItemCoverContainer {
        ${media.tabletDown`
            padding: 0;
        `}
    }
`

const ItemLayout = ({
    viewer,
    entity,
    refetchQueries,
    canUpvote,
    onEdit,
    onAfterDelete,
    options = {
        author: true,
        publishDate: true,
        views: true,
        save: true,
        likes: true,
        follow: true,
        share: true,
        comments: true,
    },
    formFields,
}) => {
    const { t } = useTranslation()
    const { site } = useSiteStore()

    const {
        localTitle,
        title,
        group,
        featured,
        localRichDescription,
        richDescription,
        tags,
        tagCategories,
        statusPublished,
        commentCount,
        suggestedItems,
        isTranslationEnabled,
        backgroundColor,
    } = entity

    const hasTranslations = !!localTitle && isTranslationEnabled
    const [isTranslated, setIsTranslated] = useState(hasTranslations)

    const translatedTitle = isTranslated ? localTitle : title
    const translatedRichDescription = isTranslated
        ? localRichDescription
        : richDescription

    const isPublished = statusPublished === 'published'
    const hasSuggestedItems = suggestedItems?.length > 0

    // White background won't activate custom theme
    const customTheme =
        backgroundColor && !['#ffffff', 'white'].includes(backgroundColor)

    const showComments = options.comments
    const showShare =
        options.share && window.__SETTINGS__.enableSharing && isPublished

    const extraField = (guid, title, fieldType, value, isFullWidth) => {
        return (
            <Col mobileLandscapeUp={isFullWidth ? 1 : 1 / 2} key={guid}>
                {isFullWidth ? (
                    <H2 style={{ marginBottom: '4px' }}>{title}</H2>
                ) : (
                    <Text size="small" variant="grey">
                        {title}
                    </Text>
                )}
                <Text size={isFullWidth ? 'normal' : 'small'}>
                    {fieldType === 'dateField' ? (
                        <p>{showFullDate(value)}</p>
                    ) : fieldType === 'dateTimeField' ? (
                        <p>{showDateTime(value)}</p>
                    ) : ['multiSelectField', 'checkboxField'].includes(
                          fieldType,
                      ) ? (
                        <p>
                            {value.split(',').map((v, i) => (
                                <span key={v}>
                                    {i > 0 && ', '}
                                    {v}
                                </span>
                            ))}
                        </p>
                    ) : fieldType === 'htmlField' ? (
                        <TiptapView
                            content={value}
                            textSize={isFullWidth ? 'normal' : 'small'}
                        />
                    ) : (
                        <p>{value}</p>
                    )}
                </Text>
            </Col>
        )
    }

    const titlePlaceholderFieldIndex = formFields.findIndex(
        (field) => field.fieldType === 'titlePlaceholderField',
    )
    const orderedContent = formFields.map(
        ({ guid, title, fieldType }, index) => {
            if (fieldType === 'titlePlaceholderField') {
                return (
                    <Col key={index}>
                        <H1
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                            }}
                        >
                            {translatedTitle}
                            <ItemStatusTag
                                entity={entity}
                                style={{
                                    marginTop: '8px',
                                }}
                            />
                        </H1>
                    </Col>
                )
            } else if (fieldType === 'richDescriptionPlaceholderField') {
                return (
                    <Col key={index}>
                        <TiptapView content={translatedRichDescription} />
                        {hasTranslations && (
                            <ToggleTranslation
                                isTranslated={isTranslated}
                                setIsTranslated={setIsTranslated}
                                style={{ marginTop: '12px' }}
                            />
                        )}
                    </Col>
                )
            } else {
                const value = entity.formResponse.find(
                    (r) => r.guid === guid,
                )?.value

                if (!value) return null

                const isFullWidth = index > titlePlaceholderFieldIndex

                return extraField(guid, title, fieldType, value, isFullWidth)
            }
        },
    )
    return (
        <ThemeProvider
            theme={customTheme && backgroundColor}
            bodyColor={backgroundColor || '#FFFFFF'}
        >
            <Document title={translatedTitle} containerTitle={group?.name} />

            <Wrapper grow>
                <Container size="large" className="ItemCoverContainer">
                    <CoverImage featured={featured} />
                </Container>
                <Container size="normal">
                    <ItemHeader
                        site={site}
                        viewer={viewer}
                        entity={entity}
                        onEdit={onEdit}
                        options={options}
                        onAfterDelete={onAfterDelete}
                        refetchQueries={refetchQueries}
                    />

                    <article
                        style={{
                            position: 'relative',
                            marginTop: '32px',
                        }}
                    >
                        <Row $spacing={16}>{orderedContent}</Row>

                        <ItemTags
                            showCustomTags={site.showCustomTagsInDetail}
                            showTags={site.showTagsInDetail}
                            customTags={tags}
                            tagCategories={tagCategories}
                            style={{ marginTop: '24px' }}
                        />

                        {isPublished && viewer.loggedIn && (
                            <LikeAndBookmark
                                entity={entity}
                                options={options}
                            />
                        )}

                        {showShare && <SocialShare />}
                    </article>
                </Container>

                <Container size={hasSuggestedItems ? 'large' : 'normal'}>
                    <Row style={{ marginTop: '32px' }}>
                        <Col tabletUp={hasSuggestedItems ? 2 / 3 : 1}>
                            {showComments && (
                                <>
                                    <H4 as="h2">
                                        {t('comments.count-comments', {
                                            count: commentCount,
                                        })}
                                    </H4>
                                    <CommentList
                                        viewer={viewer}
                                        entity={entity}
                                        refetchQueries={refetchQueries}
                                        canUpvote={canUpvote}
                                    />
                                </>
                            )}
                        </Col>
                        {hasSuggestedItems && (
                            <Col tabletUp={1 / 3}>
                                <ItemSuggestedItems entity={entity} />
                            </Col>
                        )}
                    </Row>
                </Container>
            </Wrapper>
        </ThemeProvider>
    )
}

export default ItemLayout
